import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLms } from '../../context/LmsContext';
import { ButtonLink, Card, DataTable, PageHeader, ProgressBar, StatusBadge } from '../../components/ui';
import { assignmentGrade, byId, derivedAssignmentStatus, fullName, getAssignmentProgress, getCourse, getLessonsForVersion, getQuizzesForVersion, trainerNames, userIsCourseTrainer } from '../../utils/lmsSelectors';

function matches(text, query) {
  return String(text || '').toLowerCase().includes(query.trim().toLowerCase());
}

export default function TrainerCourses() {
  const { data, currentUser, deleteCourse } = useLms();
  const [assignmentSearch, setAssignmentSearch] = useState('');
  const [assignmentStatus, setAssignmentStatus] = useState('all');
  const canDeleteCourses = currentUser?.role === 'admin' || (currentUser?.roles || []).includes('admin');
  const courses = data.courses.filter((course) => currentUser.role === 'admin' || userIsCourseTrainer(data, course, currentUser.id));
  const trainerCourseIds = courses.map((course) => course.id);
  const rows = courses.map((course) => ({
    ...course,
    version: byId(data.courseVersions, course.currentVersionId)?.versionNumber || 1,
    lessons: getLessonsForVersion(data, course.currentVersionId).length,
    quizzes: getQuizzesForVersion(data, course.currentVersionId).length,
    assignedLearners: data.assignments.filter((assignment) => assignment.courseId === course.id && assignment.status !== 'revoked').length,
    trainers: trainerNames(data, course)
  }));

  const learnerRows = data.assignments
    .filter((assignment) => trainerCourseIds.includes(assignment.courseId))
    .map((assignment) => {
      const learner = byId(data.users, assignment.assignedToUserId);
      const course = getCourse(data, assignment.courseId);
      return {
        ...assignment,
        learner: fullName(learner),
        email: learner?.email || '',
        team: learner?.team || '',
        course: course?.title || 'Unknown course',
        statusValue: derivedAssignmentStatus(data, assignment),
        progress: getAssignmentProgress(data, assignment).percent,
        grade: assignmentGrade(data, assignment)
      };
    })
    .filter((row) => {
      const statusMatch = assignmentStatus === 'all' || row.statusValue === assignmentStatus;
      const textMatch = !assignmentSearch || matches(`${row.learner} ${row.email} ${row.team} ${row.course} ${row.statusValue} ${row.dueDate}`, assignmentSearch);
      return statusMatch && textMatch;
    });

  const handleDeleteCourse = (course) => {
    if (!window.confirm(`Delete course "${course.title}"? This cannot be undone.`)) return;
    const result = deleteCourse(course.id, currentUser.id);
    if (!result.ok) window.alert(result.message);
  };

  return (
    <>
      <PageHeader title="My courses" description="Create draft courses, edit content, submit for review, and manage published training." action={<ButtonLink to="/trainer/courses/new">New course</ButtonLink>} />
      <Card>
        <DataTable
          rows={rows}
          columns={[
            { key: 'title', label: 'Course', render: (row) => <Link className="text-link strong-link" to={`/trainer/courses/${row.id}/builder`}>{row.title}</Link> },
            { key: 'category', label: 'Category' },
            { key: 'status', label: 'Status', render: (row) => <StatusBadge status={row.status} /> },
            { key: 'version', label: 'Version', render: (row) => `v${row.version}` },
            { key: 'lessons', label: 'Lessons' },
            { key: 'quizzes', label: 'Quizzes' },
            { key: 'assignedLearners', label: 'Assigned learners' },
            { key: 'trainers', label: 'Trainer/s' },
            { key: 'action', label: 'Action', render: (row) => (
              <div className="button-row compact">
                <Link className="button button-secondary" to={`/trainer/courses/${row.id}/builder`}>Builder</Link>
                {canDeleteCourses && <button className="button button-danger" type="button" onClick={() => handleDeleteCourse(row)}>Delete</button>}
              </div>
            ) }
          ]}
        />
      </Card>

      <Card>
        <div className="section-heading">
          <div>
            <h2>Learners assigned to my courses</h2>
            <p className="muted small">Showing {learnerRows.length} assigned learner record(s) for courses where you are the trainer.</p>
          </div>
        </div>
        <div className="assignment-filter-bar">
          <input className="search-input" placeholder="Search learner, email, team, course, status, or due date" value={assignmentSearch} onChange={(e) => setAssignmentSearch(e.target.value)} />
          <select value={assignmentStatus} onChange={(e) => setAssignmentStatus(e.target.value)}>
            <option value="all">All statuses</option>
            <option value="not_started">Not Started</option>
            <option value="in_progress">In Progress</option>
            <option value="needs_review">Needs Review</option>
            <option value="completed">Completed</option>
            <option value="overdue">Overdue</option>
            <option value="revoked">Revoked</option>
          </select>
        </div>
        <DataTable
          rows={learnerRows}
          columns={[
            { key: 'learner', label: 'Learner' },
            { key: 'email', label: 'Email' },
            { key: 'team', label: 'Team' },
            { key: 'course', label: 'Course' },
            { key: 'dueDate', label: 'Due date' },
            { key: 'statusValue', label: 'Status', render: (row) => <StatusBadge status={row.statusValue} /> },
            { key: 'progress', label: 'Progress', render: (row) => <ProgressBar value={row.progress} /> },
            { key: 'grade', label: 'Grade', render: (row) => row.grade === null ? '—' : `${row.grade}%` }
          ]}
          emptyMessage="No assigned learners match the current filters."
        />
      </Card>
    </>
  );
}
